import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faShuffle,
  faUserPlus,
  faGamepad,
} from '@fortawesome/free-solid-svg-icons';
import type { ConfigureData } from './SmartQuizSetup';
import './SmartQuiz.css';

const playerOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: 'Unlimited', value: 0 },
];

const timeOptions = [
  { label: '10 sec', value: 10 },
  { label: '20 sec', value: 20 },
  { label: '30 sec', value: 30 },
  { label: '60 sec', value: 60 },
];

interface Props {
  data: ConfigureData;
  onChange: (d: ConfigureData) => void;
  onBack: () => void;
  onNext: () => void;
}

const StepConfigure: React.FC<Props> = ({ data, onChange, onBack, onNext }) => {
  const update = (partial: Partial<ConfigureData>) =>
    onChange({ ...data, ...partial });

  return (
    <div className="sq-step2">
      {/* Left column: cover + title */}
      <div className="sq-step2__left">
        <div className="sq-step2__title-card">
          <label className="sq-label">Game Title *</label>
          <input
            className="sq-input"
            type="text"
            placeholder="World Capitals Quiz"
            value={data.gameTitle}
            maxLength={80}
            onChange={(e) => update({ gameTitle: e.target.value })}
          />
          <span className="sq-step2__char-count">
            {data.gameTitle.length}/80
          </span>
        </div>

        <div className="sq-step2__cover-card">
          <h4 className="sq-label">Cover Image</h4>
          <div className="sq-step2__cover-preview">
            <FontAwesomeIcon icon={faStar} className="sq-step2__cover-icon" />
            <button className="sq-step2__cover-change">🖼 Change</button>
          </div>
        </div>
      </div>

      {/* Right column: settings */}
      <div className="sq-step2__right">
        <div className="sq-step2__settings-card">
          <h3 className="sq-step2__settings-title">Game Settings</h3>

          <div className="sq-step2__setting-row">
            <div>
              <label className="sq-label">Max Players</label>
              <div className="sq-pills">
                {playerOptions.map((o) => (
                  <button
                    key={o.value}
                    className={`sq-pill ${data.maxPlayers === o.value ? 'sq-pill--active' : ''}`}
                    onClick={() => update({ maxPlayers: o.value })}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="sq-label">Time per Question</label>
              <div className="sq-pills">
                {timeOptions.map((o) => (
                  <button
                    key={o.value}
                    className={`sq-pill ${data.timePerQuestion === o.value ? 'sq-pill--active' : ''}`}
                    onClick={() => update({ timePerQuestion: o.value })}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="sq-step2__toggle-row">
            <div className="sq-step2__toggle-item">
              <FontAwesomeIcon
                icon={faShuffle}
                className="sq-step2__toggle-icon"
              />
              <span>Randomise Order</span>
              <label className="sq-toggle">
                <input
                  type="checkbox"
                  checked={data.randomiseOrder}
                  onChange={(e) => update({ randomiseOrder: e.target.checked })}
                />
                <span className="sq-toggle__slider" />
              </label>
            </div>
            <div className="sq-step2__toggle-item">
              <FontAwesomeIcon
                icon={faUserPlus}
                className="sq-step2__toggle-icon"
              />
              <span>Allow Late Joiners</span>
              <label className="sq-toggle">
                <input
                  type="checkbox"
                  checked={data.allowLateJoiners}
                  onChange={(e) =>
                    update({ allowLateJoiners: e.target.checked })
                  }
                />
                <span className="sq-toggle__slider" />
              </label>
            </div>
            <div className="sq-step2__toggle-item">
              <FontAwesomeIcon
                icon={faGamepad}
                className="sq-step2__toggle-icon"
              />
              <span>Interactive Mode</span>
              <label className="sq-toggle">
                <input
                  type="checkbox"
                  checked={data.interactiveMode}
                  onChange={(e) =>
                    update({ interactiveMode: e.target.checked })
                  }
                />
                <span className="sq-toggle__slider" />
              </label>
            </div>
          </div>

          <div className="sq-step2__actions">
            <button className="sq-btn sq-btn--outline" onClick={onBack}>
              Back
            </button>
            <button className="sq-btn sq-btn--primary" onClick={onNext}>
              Next: Generate Questions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepConfigure;
